import { useEffect, useState } from "react";
import type { buildThemeClasses } from "../theme";
import { formatMoney, LOST_REASON_LABEL, ONBOARDING_STATUS_LABEL, PAYMENT_STATUS_LABEL, type Deal } from "../services/dealsService";
import { fetchCrmObject, type CrmObject } from "../services/crmObjectsService";
import { requiresQuote } from "../services/leadsService";
import type { DealDemoPointer } from "../services/dealDemoService";
import { DealJourney } from "../components/DealJourney";
import { AttachmentsList } from "../components/AttachmentsList";
import { useSafeNavigate } from "../hooks/useSafeNavigate";
import { newQuotePath } from "../services/quotePaths";

// ===========================================================================
// Ficha de un negocio (épica #1318, issue #1316). Se abre al tocar una tarjeta
// del tablero de negocios. Mismo trazo plano que la ficha de un lead
// (PipelineBoard → LeadDetail): distintivos arriba, datos en dos columnas y
// secciones separadas por una línea.
//
// De sólo lectura, igual que el tablero: con HubSpot como fuente de verdad la
// etapa se cambia allá.
//
// En CC-CONSOLE la ficha lleva además la sección «Demo» (cuenta de demostración
// del negocio, épica #1318). Aquí no se monta: la opera la consola central a
// través de su hub de plataformas, y esta plataforma es el lado que recibe la
// cuenta. Las props de la demo se conservan para no divergir de quien la llama.
// Ésa es la ÚNICA diferencia con la ficha de CC-CONSOLE (decisión de Isaías,
// 2026-10-31): todo lo demás, incluidos los montos cotizados, es idéntico.
// ===========================================================================

type TC = ReturnType<typeof buildThemeClasses>;

const PRODUCT_TONE: Record<string, string> = {
  "Coding Cloud": "bg-sky-500/15 text-sky-600",
  Wayak: "bg-indigo-500/15 text-indigo-500",
  Nuvex: "bg-emerald-500/15 text-emerald-600",
  Movik: "bg-orange-500/15 text-orange-600",
};

/** ISO → «17 oct 2026» (o null si no hay fecha). */
function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
}

/** «Valor · fecha» o solo el valor; null cuando no hay nada. */
function withDate(value: string | null | undefined, iso: string | null | undefined): string | null {
  const date = shortDate(iso);
  if (!value) return date;
  return date ? `${value} · ${date}` : value;
}

/** Lo que se muestra de la cotización aceptada: «Título · $monto» si se puede leer; si no, su id. */
function useAcceptedQuoteLabel(quoteId: string | null | undefined): string | null {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setLabel(null);
    if (!quoteId) return undefined;
    fetchCrmObject<CrmObject & { title?: string | null; amount?: number | null }>("quotes", quoteId)
      .then((q) => {
        if (!alive || !q) return;
        const parts = [q.title || null, typeof q.amount === "number" ? formatMoney(q.amount) : null].filter(Boolean);
        setLabel(parts.length ? parts.join(" · ") : null);
      })
      .catch(() => {
        /* sin lectura: queda el id */
      });
    return () => {
      alive = false;
    };
  }, [quoteId]);
  return quoteId ? label || quoteId : null;
}

function Field({ tc, label, value }: { tc: TC; label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className={`text-xs ${tc.textSecondary}`}>{label}</p>
      <p className={`text-sm ${tc.text}`}>{value || "—"}</p>
    </div>
  );
}

export default function DealDetail({
  deal,
  tc,
}: {
  deal: Deal;
  tc: TC;
  isDarkMode?: boolean;
  /** Administrador de plataforma (lo usa la sección «Demo» en CC-CONSOLE). */
  isAdmin?: boolean;
  onDemoChanged?: (demo: DealDemoPointer | null) => void;
}) {
  const stage = deal.hubspotStageLabel || deal.stage;
  const closing = deal.closing;
  const quoteLabel = useAcceptedQuoteLabel(closing?.quoteId);
  const navigate = useSafeNavigate();
  return (
    <div className="space-y-4" data-testid="deal-detail">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-500/15 px-2.5 py-1 text-xs font-medium text-primary-500">
          <span className="h-2 w-2 rounded-full bg-primary-500" /> {stage}
        </span>
        {deal.product && (
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${PRODUCT_TONE[deal.product] ?? "bg-gray-400/15 text-gray-500"}`}>
            {deal.product}
          </span>
        )}
        <span className="text-sm font-bold text-primary-500">{formatMoney(deal.amount)}</span>
        {/* Armador de cotizaciones (#1545): hereda empresa, contacto y producto del negocio. */}
        {requiresQuote(deal.product) && (
          <button
            onClick={() => navigate(newQuotePath({ dealId: deal.id }))}
            data-testid="deal-create-quote"
            className="ml-auto inline-flex items-center gap-1 rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-400"
          >
            <i className="ri-file-add-line" /> Crear cotización
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field tc={tc} label="Empresa" value={deal.company} />
        <Field tc={tc} label="Contacto" value={deal.contact} />
        <Field tc={tc} label="Pipeline" value={deal.hubspotPipelineLabel} />
        <Field tc={tc} label="Origen" value={deal.source} />
        <Field tc={tc} label="Cierre esperado" value={deal.closeDate ? String(deal.closeDate).slice(0, 10) : null} />
        <Field tc={tc} label="Creado" value={deal.createdAt} />
        {deal.quotedMonthly ? <Field tc={tc} label="Mensualidad cotizada (sin IVA)" value={formatMoney(deal.quotedMonthly)} /> : null}
        {deal.quotedOneTime ? <Field tc={tc} label="Pago único cotizado (sin IVA)" value={formatMoney(deal.quotedOneTime)} /> : null}
      </div>

      {deal.amountNote && (
        <div data-testid="deal-amount-note">
          <p className={`text-xs ${tc.textSecondary}`}>Cómo se calculó el valor{deal.quoteAttachmentName ? ` (de ${deal.quoteAttachmentName})` : ""}</p>
          <p className={`text-sm ${tc.text}`}>{deal.amountNote}</p>
        </div>
      )}

      {deal.notes && (
        <div>
          <p className={`text-xs ${tc.textSecondary}`}>Notas</p>
          <p className={`whitespace-pre-wrap text-sm ${tc.text}`}>{deal.notes}</p>
        </div>
      )}

      {/* Cierre (#1524): cotización aceptada, pago, licencia, onboarding y, si
          se perdió, el motivo y cuándo volver a intentarlo. Lo escribe el
          backend al aceptar la cotización / activar la licencia. */}
      <div className={`border-t pt-4 ${tc.border}`} data-testid="deal-detail-closing">
        <h3 className={`mb-3 text-sm font-bold ${tc.text}`}>Cierre</h3>
        <div className="grid grid-cols-2 gap-3">
          {/* B2C (Movik, #1536): se cierra con la licencia de la app, sin cotización. */}
          <Field
            tc={tc}
            label="Cotización aceptada"
            value={requiresQuote(deal.product) || quoteLabel ? withDate(quoteLabel, closing?.quoteAcceptedAt) : "No aplica (B2C: cierra con la licencia de la app)"}
          />
          <Field tc={tc} label="Pago" value={withDate(closing?.paymentStatus ? PAYMENT_STATUS_LABEL[closing.paymentStatus] : null, closing?.paidAt)} />
          <Field tc={tc} label="Licencia" value={closing?.licenseSlug} />
          <Field tc={tc} label="Onboarding" value={withDate(closing?.onboardingStatus ? ONBOARDING_STATUS_LABEL[closing.onboardingStatus] : null, closing?.activatedAt)} />
          {(closing?.lostReason || deal.stage === "perdido") && (
            <Field
              tc={tc}
              label="Motivo de pérdida"
              value={
                closing?.lostReason
                  ? `${LOST_REASON_LABEL[closing.lostReason]}${closing.lostReasonNote ? ` — ${closing.lostReasonNote}` : ""}`
                  : deal.lostReason || null
              }
            />
          )}
          {closing?.followUpAt && <Field tc={tc} label="Próximo seguimiento" value={shortDate(closing.followUpAt)} />}
          {closing?.note && <Field tc={tc} label="Nota" value={closing.note} />}
        </div>
      </div>

      {/* Recorrido por el pipeline: cada etapa por la que pasó en HubSpot. */}
      <div className={`border-t pt-4 ${tc.border}`} data-testid="deal-detail-journey">
        <h3 className={`mb-3 text-sm font-bold ${tc.text}`}>Recorrido en el pipeline</h3>
        <DealJourney deal={deal} textClass={tc.text} mutedClass={tc.textSecondary} borderClass={tc.border} />
      </div>

      {/* Documentos: los adjuntos de HubSpot del negocio y de su contacto. */}
      <div className={`border-t pt-4 ${tc.border}`} data-testid="deal-detail-documents">
        <h3 className={`mb-3 text-sm font-bold ${tc.text}`}>Documentos</h3>
        <AttachmentsList dealId={deal.id} textClass={tc.text} mutedClass={tc.textSecondary} borderClass={tc.border} />
      </div>
    </div>
  );
}
